import React, { createContext, useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getRoleFromToken } from "../utils/auth";

const AuthContext = createContext();

const decodeToken = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

const isTokenExpired = (token) => {
  if (!token) return true;
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
};

const AuthContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const currentUser = useSelector((state) => state.auth.currentUser || state.user.updatedUser);

  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [localToken, setLocalToken] = useState(() => {
    return localStorage.getItem("token") || null;
  });

  const [localRole, setLocalRole] = useState(null);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setLocalToken(null);
    setUser(null);
    setLocalRole(null);
    console.log("Logged out successfully");
  };

  useEffect(() => {
    const syncAuthState = () => {
      const currentToken = token || localStorage.getItem("token");
      if (currentToken) {
        if (isTokenExpired(currentToken)) {
          logout();
        } else {
          setLocalToken(currentToken);
          localStorage.setItem("token", currentToken);
          const role = getRoleFromToken(currentToken);
          setLocalRole(role);
        }
      } else {
        logout();
      }

      const updatedUser = currentUser || (localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null); // تغيير detailsUser إلى currentUser
      if (updatedUser) {
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
      } else if (!currentToken) {
        setUser(null);
        localStorage.removeItem("user");
      }
    };

    syncAuthState();

    const handleStorageChange = (event) => {
      if (event.key === "token") {
        const newToken = event.newValue || null;
        setLocalToken(newToken);
      }
      if (event.key === "user") {
        const newUser = event.newValue ? JSON.parse(event.newValue) : null;
        setUser(newUser);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    const intervalId = setInterval(() => {
      if (localToken && isTokenExpired(localToken)) {
        logout();
      }
    }, 60000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(intervalId);
    };
  }, [dispatch, token, currentUser]);

  const contextValue = useMemo(
    () => ({
      user,
      token: localToken,
      role: localRole,
      isTokenExpired: () => isTokenExpired(localToken),
      logout,
    }),
    [user, localToken, localRole]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthContextProvider };
